import React, { Component } from "react";
import { Grid, Icon, Modal } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import CheckBox from 'components/final-form/CheckBox';
import { debounce } from 'ts-debounce';
import { GuarantorDto, GuarantorSearchRequest, UpsertGuarantorDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { deleteGuarantor, findGuarantorById, searchGuarantors } from 'services/guarantorServices';
import UpsertGuarantorForm from 'components/guarantors/UpsertGuarantorForm';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import axios, { CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const initialGuarantorsSearch: Partial<GuarantorSearchRequest> = {
  includesInactive: false
};

interface GuarantorFieldProps extends FormState {
  values: Partial<GuarantorSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundGuarantors: Array<GuarantorDto>,
  guarantorsLoaded: boolean,
  addEditModalOpen: boolean,
  guarantor?: UpsertGuarantorDto,
  guarantorId?: number,
  searchValues: Partial<GuarantorSearchRequest>,
  cancelTokenSource: CancelTokenSource,
}

class GuarantorsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundGuarantors: [],
      guarantorsLoaded: false,
      addEditModalOpen: false,
      guarantor: undefined,
      guarantorId: undefined,
      searchValues: initialGuarantorsSearch,
      cancelTokenSource
    };

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findGuarantorById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, guarantor?: UpsertGuarantorDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        guarantor: guarantor,
        guarantorId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      guarantorId: undefined,
      guarantor: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        guarantorsLoaded: true
      })
    };

    searchGuarantors(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundGuarantors: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  deleteGuarantor = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteGuarantor(id, cancelTokenSource).then(response => {
      this.setState(({foundGuarantors}: State) => ({
        foundGuarantors: foundGuarantors.filter((guarantor: GuarantorDto) => guarantor.id != response.id)
      }))
    }).catch(noop);
  };

  handleChange = debounce(({values}: GuarantorFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const onFinally = () => {
      this.setState({
        guarantorsLoaded: true
      })
    };

    const {cancelTokenSource} = this.state;
    this.setState({guarantorsLoaded: false});
    searchGuarantors(values, cancelTokenSource)
        .then(response =>
            this.setState({
              foundGuarantors: response,
              searchValues: values
            })
        )
        .catch(noop)
        .finally(onFinally);
  }, 300);

  render(): React.ReactNode {
    const {guarantorsLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchGuarantorSegment()}
          </div>
          <StyledViewTableRow>
            {!guarantorsLoaded
                ? <LoaderComponent message={getMessage("guarantor.loading")}/>
                : this.renderGuarantorsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertGuarantorModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchGuarantorSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              includesInactive: false
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {foundGuarantors} = this.state;

    const numResults = foundGuarantors.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("city")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="city"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} textAlign="right">
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("guarantor.confirmDelete")}
              onConfirm={() => this.deleteGuarantor(cellData)}
          />
        </React.Fragment>
    );
  };

  guarantorsRowGetter = ({index}: any) => {
    const {foundGuarantors} = this.state;

    Object.assign(foundGuarantors[index], {index: index + 1});

    return foundGuarantors[index];
  };

  guarantorsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundGuarantors} = this.state;

    const guarantorId = foundGuarantors[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(guarantorId)}
        >
          {columns}
        </div>
    );
  };

  tariffTypeCellRenderer = ({cellData}: any) => {

    const {getMessage} = this.props;

    if (cellData) {

      return getMessage(`tariffType.${cellData.toLowerCase()}`)
    }
  }

  notificationCellRenderer = ({cellData}: any) => {
    if (cellData) {
      return <Icon name="check" size="small"/>
    }
    return ""
  }

  renderGuarantorsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundGuarantors} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundGuarantors.length}
            rowGetter={this.guarantorsRowGetter}
            rowRenderer={this.guarantorsRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 300,
                label: (getMessage("city")),
                dataKey: "city"
              },
              {
                width: 200,
                label: (getMessage("street")),
                dataKey: "street"
              },
              {
                width: 200,
                label: (getMessage("bagNumber")),
                dataKey: "bagNumber"
              },
              {
                width: 200,
                label: (getMessage("tariff")),
                dataKey: "tariffType",
                cellRenderer: (this.tariffTypeCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("active")),
                dataKey: "active",
                cellRenderer: this.notificationCellRenderer
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };

  renderUpsertGuarantorModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, guarantor, guarantorId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("guarantors")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertGuarantorForm
                guarantor={guarantor}
                guarantorId={guarantorId}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };
}

export default withMessages(GuarantorsView);
